import React from 'react';
import lodash from 'lodash';
import { Button, Card, Table } from 'antd';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import IntlMessages from 'util/IntlMessages';
import { injectIntl } from 'react-intl';
import { ENDPOINTS } from '@constants/UHEEndpoints';
import UheHelper from 'util/UheHelper';

import ListingsTopFilter from '@filters/ListingsTopFilter';
import ListingsTableInputFilter from '@filters/ListingsTableInputFilter';
import OrganizationCell from '@components/tables/cells/OrganizationCell';
import CustomerCell from '@components/tables/cells/CustomerCell';
import FacilityCell from '@components/tables/cells/FacilityCell';
import ActionsCell from '@components/tables/cells/ActionsCell';
import NumberCell from '@components/tables/cells/NumberCell';
import ExportCsv from '@components/ExportCsv/ExportCsv';
import {
  onFetcFacilitieshData,
  deleteFacility,
  clearFacilityState,
} from '@uhe_actions/configuration/facilities/FacilitiesActions';
import { setSubtitle } from '@uhe_actions/SubtitleActions';

import {
  LISTING_TABLES_PAGE_SIZE,
  TOP_FILTER_PREFIX,
  TABLE_FILTER_PREFIX,
  APP_PAGES_CONTEXT, IS_EDIT_PAGE_REGEX,
} from '@constants/UHESettings';
import { withLastLocation } from 'react-router-last-location';
import {
  getColumnSorting, setTableData, getCurrentSort, saveDefaultSortOnClick,
} from '@util/UheHelper';
import { shouldResetPaginationSuccess } from '@actions';
import { DownOutlined } from '@ant-design/icons';

/**
 * Renders facilities table
 */
class Facilities extends React.Component {
  /**
   * Adapts the data from the API
   * @param {Array} data of list
   * @returns {Array} formated list
   */
  static dataAdapter(data = []) {
    const adaptedData = [];

    data.forEach((value, index) => {
      adaptedData.push({
        key: index,
        organization: {
          id: value.customer.organization.id,
          name: value.customer.organization.name,
        },
        customer: {
          id: value.customer.id,
          organizationId: value.customer.organization.id,
          name: value.customer.name,
        },
        facility: {
          id: value.id,
          organizationId: value.customer.organization.id,
          customerId: value.customer.id,
          name: value.name,
        },
        street: value.address1,
        city: value.city,
        state: value.state,
        zip: value.zip,
        bedCartNo: value.total_endpoints + value.total_mobile_users,
        configuredApsNo: value.total_configured_endpoints,
        sbtDevicesNo: value.total_endpoints_sbt,
        byodNo: value.total_mobile_users,
        actions: {
          id: value.id,
        },
      });
    });

    return adaptedData;
  }

  /**
   * Facilities Constructor
   * @param {object} props Facilities Props
   */
  constructor(props) {
    super(props);
    this.onPageChange = this.onPageChange.bind(this);
    this.setTableData = setTableData.bind(this);
    const {
      history, subtitle, setSubtitle,
    } = this.props;

    this.state = {
      tableData: {
        organization: {
          currSort: 'asc',
          isClicked: false,
        },
        customer: {
          currSort: 'asc',
          isClicked: false,
        },
        facility: {
          currSort: 'asc',
          isClicked: false,
        },
        street: {
          currSort: 'asc',
          isClicked: false,
        },
        city: {
          currSort: 'asc',
          isClicked: false,
        },
        state: {
          currSort: 'asc',
          isClicked: false,
        },
        zip: {
          currSort: 'asc',
          isClicked: false,
        },
      },
      showMore: 2,
    };

    this.data = [];
    this.columns = [];
    this.filterTypes = {};
    this.topFilterMap = {
      [`${TOP_FILTER_PREFIX}organization`]: 'customer.organization.id',
      [`${TOP_FILTER_PREFIX}customer`]: 'customer.id',
      [`${TOP_FILTER_PREFIX}facility`]: 'id',
    };
    this.tableFilterMap = {
      organization: 'customer.organization.name',
      customer: 'customer.name',
      facility: 'name',
      street: 'address1',
      city: 'city',
      state: 'state',
      zip: 'zip',
    };
    this.tableKeys = [
      'organization',
      'customer',
      'facility',
      'street',
      'city',
      'state',
      'zip',
      'bedCartNo',
      'configuredApsNo',
      'sbtDevicesNo',
      'byodNo',
      'actions',
    ];
    const { location } = props;
    const { tableData } = this.state;
    this.tableKeys.forEach((value, index) => {
      this.columns.push({
        title: (cellData) => (
          <ListingsTableInputFilter
            cellData={cellData}
            title={`uhe.table.${value}`}
            setTableData={this.setTableData}
            tableData={tableData}
            dataKey={value}
            showFilter={
              !(
                value === 'actions'
                || value === 'bedCartNo'
                || value === 'configuredApsNo'
                || value === 'sbtDevicesNo'
                || value === 'byodNo'
                || value === 'iConsultNo'
                || value === 'iObserverNo'
              )
            }
            triggerCharsNum={value === 'street' || value === 'state' ? 0 : undefined}
          />
        ),
        sorter: (value === 'actions'
          || value === 'bedCartNo'
          || value === 'iConsultNo'
          || value === 'iObserverNo'
          || value === 'configuredApsNo'
          || value === 'sbtDevicesNo'
          || value === 'byodNo')
          ? false : { multiple: index },
        defaultSortOrder: (value) ? getColumnSorting(value, location) : false,
        align: index > 3 ? 'center' : 'left',
        minWidth: 200,
        dataIndex: value,
        render: (content) => this.cellRenderer(content, value),
      });
    });

    this.topFilters = [
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byOrganization',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'organization',
      },
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byCustomer',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'customer',
      },
    ];

    this.history = history;
    this.qParams = new URLSearchParams(this.history.location.search);
    this.defaultSorted = true;

    if (
      subtitle && subtitle.langId !== 'facilities.title'
    ) {
      setSubtitle('facilities.title');
    }

    this.onPageChange = this.onPageChange.bind(this);
    this.setTableData = setTableData.bind(this);
    this.tableFooterRef = React.createRef();
  }

  /**
   * componentDidMount() is invoked immediately after
   * a component is mounted (inserted into the tree)
   * @returns {void} void
   */
  componentDidMount() {
    this.tableFooterRef.current.parentNode.parentNode.addEventListener('scroll', this.handleScroll, true);
    const {
      lastLocation,
      pagination,
      shouldResetPaginationFlag,
      resetPaginationSuccess,
    } = this.props;

    if (!IS_EDIT_PAGE_REGEX.test(lastLocation?.pathname) || shouldResetPaginationFlag) {
      this.onPageChange(1);
      resetPaginationSuccess();
      return;
    }

    this.onPageChange(pagination?.current || 1);
  }

  /**
   * Updates table on location change
   * @param {Object} prevProps previous props
   * @returns {void} void
   */
  componentDidUpdate(prevProps) {
    const { location } = this.props;
    if (location.search !== prevProps.location.search) {
      this.qParams = new URLSearchParams(location.search);
      this.onPageChange(1);
    }
  }

  /**
   * Remove Event Listener
   * @returns {void}
   */
  componentWillUnmount() {
    this.tableFooterRef.current.parentNode.parentNode.removeEventListener('scroll', this.handleScroll);
  } 

  /**
   * On Page Change
   * @param  {number} page Page Number
   * @return {void}
   */
  onPageChange(page) {
    const { onFetchData } = this.props;
    this.currentPage = page - 1;
    const currSort = this.qParams.getAll('sort') || [];
    const filter = [];
    const defaultSort = ['customer.organization.name,asc', 'customer.name,asc', 'name,asc', 'address1,asc', 'city,asc', 'state,asc', 'zip,asc'];

    lodash.forOwn(this.topFilterMap, (value, key) => {
      const filterParam = this.qParams.get(key);
      if (filterParam) {
        filter.push(`${value}=${filterParam}`);
      }
    });

    lodash.forOwn(this.tableFilterMap, (value, key) => {
      const filterParam = this.qParams.get(`${TABLE_FILTER_PREFIX}${key}`);

      if (filterParam) {
        if (
          this.filterTypes[key]
          && this.filterTypes[key].type === 'dropdown'
        ) {
          filter.push(`${value}=${encodeURIComponent(filterParam)}`);
        } else {
          filter.push(`${value}~=${encodeURIComponent(`%${filterParam}%`)}`);
        }
      }
    });

    let sort = getCurrentSort(currSort, this.tableFilterMap);

    if (this.defaultSorted && currSort.length === 0) {
      sort = defaultSort;
    } else if (currSort.length) {
      const customSort = getCurrentSort(currSort, this.tableFilterMap);
      const nonCustomFilteredColumns = saveDefaultSortOnClick(defaultSort, customSort);
      sort = [...customSort, ...nonCustomFilteredColumns];
      this.defaultSorted = false;
    }

    this.filter = filter;
    this.sort = sort;

    this.setState({ showMore: page + 1 });
    onFetchData(page - 1, sort, filter);
  }

  /**
   * Renders cell
   * @param {string} content cell content
   * @param {string} key cell key
   * @return {JSX} structured cell
   */
  cellRenderer(content, key) {
    let cell;
    const { intl, loggedUser } = this.props;

    switch (key) {
      case 'organization':
        cell = <OrganizationCell content={content} />;
        break;
      case 'customer':
        cell = <CustomerCell content={content} />;
        break;
      case 'facility':
        cell = <FacilityCell content={content} />;
        break;
      case 'actions':
        cell = (
          <ActionsCell
            page={APP_PAGES_CONTEXT.facilities}
            loggedUser={loggedUser}
            intl={intl}
            content={content}
            editLink={`/configuration/facilities/edit/${content.id}`}
            deleteAction={() => {
              this.props.deleteFacility(
                content.id,
                this.currentPage,
                this.sort,
                this.filter,
              );
            }}
          />
        );
        break;

      default:
        cell = <NumberCell content={content} />;
    }

    return cell;
  }

  /**
  * Render Add Button
  * @returns {JSX} add button
  */
  renderAddButton = () => {
    const { loggedUser } = this.props;
    const { isCaregilitySystemAdmin, isOrganizationAdmin, isCustomerAdmin } = loggedUser;

    if (isCaregilitySystemAdmin || isOrganizationAdmin || isCustomerAdmin) {
      return (
        <Link
          to="/configuration/facilities/new"
          shape="circle"
          icon="+"
          className="page-icons page-icon-plus"
        >
          <i className="icon icon-add" />
          <span><IntlMessages id="common.addText" /></span>
        </Link>
      );
    }

    return null;
  }

  /**
  * Render Export Csv Button
  * @returns {JSX} export button
  */
  renderExportCsvButton = () => {
    const { loggedUser } = this.props;
    const {
      isCaregilitySystemAdmin, isCaregilityAdmin, isOrganizationAdmin, isCustomerAdmin,
    } = loggedUser;

    if (isCaregilitySystemAdmin || isCaregilityAdmin || isOrganizationAdmin || isCustomerAdmin) {
      return (
        <ExportCsv
          getCsvUrl={UheHelper.getCsvUrl(
            this.filter, this.sorting, ENDPOINTS.ExportCsv.facilities,
          )}
        />
      );
    }
  }

  /**
  * Handles horizontal scroll in table
  * @param {event} event table
  * @returns {void}
  */
  handleScroll = (event) => {
    const currentScroll = event.target.scrollLeft;
    if (this.tableFooterRef.current) {
      this.tableFooterRef.current.parentNode.style.transform = `translateX(${currentScroll}px)`;
    }
  };

  /**
  * Render table
  * @returns {JSX} table
  */
  renderTable = () => {
    const {
      pagination, data, loading, loggedUser, location,
    } = this.props;
    pagination.onChange = this.onPageChange;
    const { tableData, showMore } = this.state;

    if (loggedUser.roles) {
      const { isCaregilitySystemAdmin, isOrganizationAdmin, isCustomerAdmin } = loggedUser;

      if (isCaregilitySystemAdmin || isOrganizationAdmin || isCustomerAdmin) {
        if (this.columns.length <= 7) {
          const foundActions = this.tableKeys.filter((key) => key === 'actions');

          if (foundActions.length === 0) {
            this.tableKeys.push('actions');
          }

          this.tableKeys.filter((key) => key === 'actions').forEach((value, index) => {
            this.columns.push({
              title: (cellData) => (
                <ListingsTableInputFilter
                  cellData={cellData}
                  tableData={tableData}
                  setTableData={this.setTableData}
                  title={`uhe.table.${value}`}
                  dataKey={value}
                  showFilter={!(value === 'actions')}
                  triggerCharsNum={value === 'street' || value === 'state' ? 0 : undefined}
                />
              ),
              sorter: (value === 'actions') ? false : { multiple: index },
              defaultSortOrder: (value) ? getColumnSorting(value, location) : false,
              align: index > 3 ? 'center' : 'left',
              minWidth: 200,
              dataIndex: value,
              render: (content) => this.cellRenderer(content, value),
            });
          });
        }
      }

      return (
        <Table
          bordered
          className="gx-table-responsive custom-scrollbar"
          columns={this.columns}
          dataSource={Facilities.dataAdapter(data)}
          pagination={false}
          loading={loading}
          footer={() => (
            <div className="table-footer" ref={this.tableFooterRef}>
              <Button type="text" onClick={() => this.onPageChange(showMore)}>
                <IntlMessages id="common.showMore" />
                <DownOutlined />
              </Button>
            </div>
          )}
        />
      );
    }
  }

  /**
   * Renders Facilities Component
   * @returns {JSX.Element} Facilities Component
   */
  render() {
    const { pagination } = this.props;
    pagination.onChange = this.onPageChange;

    return (
      <div className="dashboard configuration">
        <div className="customers-filter-box">
          <Card
            className="filter-boxes gx-card"
            title={<IntlMessages id="uhe.title.filters" />}
          >
            <ListingsTopFilter filters={this.topFilters} />
          </Card>
        </div>
        <div>
          <div className="uhe-table-header">
            <div className="buttons-container">
              {this.renderAddButton()}
              {this.renderExportCsvButton()}
            </div>
            <p>
              <IntlMessages id="uhe.table.matchingResults" />
              <span>{pagination.total}</span>
            </p>
          </div>
          <div className="uhe-table">
            {this.renderTable()}
          </div>
        </div>
      </div>
    );
  }
}

Facilities.defaultProps = {
  data: [],
  loading: true,
  pagination: {
    pageSize: LISTING_TABLES_PAGE_SIZE,
    defaultCurrent: 1,
  },
  lastLocation: {
    pathname: '/',
    search: '',
    hash: '',
    state: undefined,
    key: '',
  },
  shouldResetPaginationFlag: false,
};

Facilities.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  history: PropTypes.object,
  location: PropTypes.object,
  intl: PropTypes.object,
  pagination: PropTypes.object,
  subtitle: PropTypes.object,
  location: PropTypes.object,
  onFetchData: PropTypes.func,
  setSubtitle: PropTypes.func,
  lastLocation: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
    state: undefined,
    key: PropTypes.string,
  }),
  shouldResetPaginationFlag: PropTypes.bool,
  resetPaginationSuccess: PropTypes.func.isRequired,
};

/**
 * mapStateToProps function
 * @param {Object} ConfigurationUnits object
 * @param {Object} subtitle object
 * @param {Object} ConfigurationUsers object
 * @param {Object} common object
 * @returns {Object} object
 */
const mapStateToProps = ({
  ConfigurationFacilities,
  subtitle,
  ConfigurationUsers,
  common,
}) => {
  const { list, page } = ConfigurationFacilities.table || {};
  const { loading } = ConfigurationFacilities;
  const { shouldResetPagination } = common;

  const pagination = {
    total: page.totalElements || 0,
    current: page.number + 1 || 0,
    pageSize: LISTING_TABLES_PAGE_SIZE,
    defaultCurrent: 1,
  };

  return {
    data: list,
    pagination,
    loading,
    subtitle,
    loggedUser: ConfigurationUsers.ownUser,
    shouldResetPaginationFlag: shouldResetPagination,
  };
};

/**
 * mapDispatchToProps function
 * @param {Function} dispatch dispatch
 * @returns {Object} object
 */
const mapDispatchToProps = (dispatch) => ({
  deleteFacility: (id, page, sorting, filter) => dispatch(deleteFacility({
    id, page, sorting, filter,
  })),
  setSubtitle: (langId) => dispatch(setSubtitle(langId)),
  onFetchData: (page, sort, filter) => dispatch(onFetcFacilitieshData(page, sort, filter)),
  clearFacilityState: () => dispatch(clearFacilityState()),
  resetPaginationSuccess: () => dispatch(shouldResetPaginationSuccess()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(withLastLocation(withRouter(Facilities))));
